// Columna izquierda del explorer (`.col-filters` del mockup): Búsqueda · Nivel · Días de mora ·
// Compromiso · Contacto reciente. Los rótulos y las opciones son los del mockup, tal cual.
import { Search } from 'lucide-react';
import { cn } from '@/lib/cn';
import { FiltersColumn } from '@/shared/patterns/FiltersColumn';
import { TierPill } from '@/shared/patterns/Pills';
import {
    ARREARS_FILTER_OPTIONS,
    CONTACT_FILTER_OPTIONS,
    NO_TIER_FILTER,
    PROMISE_FILTER_OPTIONS,
    TIER_FILTER_VALUES,
    emptyCustomersFilters,
    type CustomersFilters,
    type TierFilterValue,
} from '../types';
import { countActiveFilters } from '../logic';

const LABEL = 'mb-[5px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const GROUP = 'mt-2.5';
const FIELD =
    'h-[26px] w-full rounded border border-border bg-card px-2 text-[11.5px] text-foreground outline-none focus-visible:border-ring';

export interface CustomersFiltersPanelProps {
    filters: CustomersFilters;
    onChange: (next: CustomersFilters) => void;
    collapsed: boolean;
    onToggleCollapsed: () => void;
}

export function CustomersFiltersPanel({
    filters,
    onChange,
    collapsed,
    onToggleCollapsed,
}: CustomersFiltersPanelProps) {
    const patch = (partial: Partial<CustomersFilters>) => {
        onChange({ ...filters, ...partial });
    };

    const toggleTier = (tier: TierFilterValue) => {
        const next = filters.tiers.includes(tier)
            ? filters.tiers.filter((item) => item !== tier)
            : [...filters.tiers, tier];
        patch({ tiers: next });
    };

    return (
        <FiltersColumn
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            activeCount={countActiveFilters(filters)}
            onReset={() => {
                onChange(emptyCustomersFilters());
            }}
            regionLabel="Filtros"
        >
            <div className={GROUP}>
                <div className={LABEL}>Búsqueda</div>
                <div className="relative">
                    <Search
                        aria-hidden
                        className="pointer-events-none absolute left-1.5 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
                    />
                    <input
                        type="search"
                        value={filters.search}
                        onChange={(event) => {
                            patch({ search: event.target.value });
                        }}
                        placeholder="Nombre, CURP, tel o CL-XXXX"
                        aria-label="Buscar por nombre, CURP, teléfono o folio de cliente"
                        className={cn(FIELD, 'pl-6 pr-2')}
                    />
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Nivel</div>
                <div className="flex flex-wrap gap-1">
                    {TIER_FILTER_VALUES.map((tier) => (
                        <TierPill
                            key={tier}
                            level={tier === NO_TIER_FILTER ? null : tier}
                            selected={filters.tiers.includes(tier)}
                            onToggle={() => {
                                toggleTier(tier);
                            }}
                        />
                    ))}
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Días de mora</div>
                <select
                    value={filters.arrears}
                    aria-label="Días de mora"
                    onChange={(event) => {
                        patch({ arrears: event.target.value as CustomersFilters['arrears'] });
                    }}
                    className={FIELD}
                >
                    {ARREARS_FILTER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Compromiso</div>
                <select
                    value={filters.promise}
                    aria-label="Compromiso"
                    onChange={(event) => {
                        patch({ promise: event.target.value as CustomersFilters['promise'] });
                    }}
                    className={FIELD}
                >
                    {PROMISE_FILTER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Contacto reciente</div>
                <select
                    value={filters.contact}
                    aria-label="Contacto reciente"
                    onChange={(event) => {
                        patch({ contact: event.target.value as CustomersFilters['contact'] });
                    }}
                    className={FIELD}
                >
                    {CONTACT_FILTER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>
        </FiltersColumn>
    );
}
